import { useState, useCallback, useEffect, useRef } from 'react';
import { createFileRoute, Link, useBlocker, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, FlaskConical, Trash2, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import { useMembership } from '@/hooks/useMembership';
import { useIsPhone } from '@/hooks/useIsPhone';
import { useIdempotencyKey } from '@/lib/idempotency';
import { PhoneApplication } from '@/components/entry/phone/PhoneApplication';
import { useDebouncedSignal } from '@/hooks/useDebouncedSignal';
import { useFarmProducts } from '@/components/pond/page/data';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import {
  NumericInput,
  SelectInput,
  TextArea,
  AutoSaveIndicator,
  type SaveStatus,
} from '@/components/entry';
import { sameFieldValue, toNumberOrNull } from '@/lib/number';
import {
  createFailureMessage,
  isUnconfirmedWrite,
  registerAutosaveFlusher,
  saveFailureMessage,
} from '@/lib/apiErrors';
import { writeSignal } from '@/lib/writeSignal';
import { useUnsavedEditsWarning } from '@/hooks/useUnsavedEditsWarning';
import { cn } from '@/lib/cn';
import { format, parse } from 'date-fns';
import { todayInPH } from '@/lib/phDate';
import { qk } from '@/lib/queryKeys';
import type { Application, ApplicationCreate, ApplicationUpdate } from '@/types/entry';
import type { ApiResponse } from '@/types/api';
import type { FarmProductResponse, PondDetailResponse } from '@/types/generated/openapi';
import { randomId } from '@/lib/randomId';

const CATEGORY_OPTIONS = [
  { value: 'probiotic', label: 'Probiotic' },
  { value: 'water_treatment', label: 'Water Treatment' },
  { value: 'chemical', label: 'Chemical' },
  { value: 'feed_additive', label: 'Feed Additive' },
  { value: 'antibiotic', label: 'Antibiotic' },
  { value: 'supplement', label: 'Supplement' },
  { value: 'other', label: 'Other' },
];

interface NewApplicationDraft {
  key: string;
  product: FarmProductResponse;
  amount: number | null;
  notes: string;
}

interface ApplicationSearchParams {
  date?: string;
}

export const Route = createFileRoute('/_app/entry/pond/$pondId/application')({
  validateSearch: (search: Record<string, unknown>): ApplicationSearchParams => {
    return {
      date: typeof search.date === 'string' ? search.date : undefined,
    };
  },
  component: ApplicationRoute,
});

function ApplicationRoute() {
  const isPhone = useIsPhone();
  const { pondId } = Route.useParams();
  const { date } = Route.useSearch();
  const { membership } = useMembership();
  if (isPhone) return <PhoneApplication farmId={membership?.farmId} pondId={pondId} date={date} />;
  if (!membership?.farmId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size={32} />
      </div>
    );
  }
  return <ApplicationForm farmId={membership.farmId} />;
}

function ApplicationForm({ farmId }: { farmId: string }) {
  const { pondId } = Route.useParams();
  const { date } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // DELETE is supervisor+; technicians would only get a 403 (#169).
  const { membership, isLoading: membershipLoading, isSupervisorOrAbove: canDelete } = useMembership();

  const dateStr = date || todayInPH();
  const displayDate = parse(dateStr, 'yyyy-MM-dd', new Date());
  const isToday = dateStr === todayInPH();

  const idempotency = useIdempotencyKey();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [draftById, setDraftById] = useState<Record<string, Partial<Application>>>({});
  const pendingChangesRef = useRef<Record<string, Partial<Application>>>({});
  const typedRef = useRef<Record<string, { amount?: number | null; notes?: string }>>({});
  const [rowKeyById, setRowKeyById] = useState<Record<string, string>>({});
  const [hasPending, setHasPending] = useState(false);
  // Products picked but not saved yet. Nothing is created until an amount > 0 is saved:
  // a selection alone must never record 0 of a product.
  const [drafts, setDrafts] = useState<NewApplicationDraft[]>([]);
  // A refused amount (message showing) leaves the draft's last good amount in place, so
  // Save would otherwise send a number the user can see was rejected.
  const [refusedDrafts, setRefusedDrafts] = useState<Record<string, boolean>>({});

  // Fetch pond data
  const { data: pond, isLoading: pondLoading } = useQuery({
    queryKey: qk.pond.detail(membership?.farmId, pondId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<PondDetailResponse>>(
        `/api/v1/farms/${membership?.farmId}/ponds/${pondId}/detail`
      );
      return response.data;
    },
    enabled: !!pondId && !!membership?.farmId,
  });

  const cycleId = pond?.active_cycle?.id;
  const applicationsKey = ['applications', cycleId, dateStr] as const;

  // The pond page journal/summary, the product use counts and the entry hub badges
  // all derive from these rows; the form's own key above doesn't prefix-match them.
  const refreshRelated = () => {
    queryClient.invalidateQueries({ queryKey: qk.products.list(farmId) });
    queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
    if (cycleId) {
      queryClient.invalidateQueries({ queryKey: qk.applications.crop(farmId, cycleId) });
    }
  };

  const resetKey = `${cycleId}-${dateStr}`;
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey);
    setDraftById({});
    setHasPending(false);
    setDrafts([]);
  }
  useEffect(() => {
    pendingChangesRef.current = {};
    typedRef.current = {};
  }, [resetKey]);

  const writeScope = { id: `application-writes-${cycleId}` };

  // Fetch applications for the day
  const { data: applications, isLoading: applicationsLoading } = useQuery({
    queryKey: applicationsKey,
    queryFn: async () => {
      const response = await api.get<{ data: Application[] }>(
        `/api/v1/farms/${membership?.farmId}/applications`,
        {
          params: {
            cycle_id: cycleId!,
            start_date: dateStr,
            end_date: dateStr,
          },
        }
      );
      return response.data || [];
    },
    enabled: !!cycleId && !!membership?.farmId,
  });

  // The farm's product list (recent first). Rows link to a product so names don't split.
  const { data: products = [], isLoading: productsLoading, isError: productsError } = useFarmProducts(farmId);
  const activeProducts = products.filter((p) => p.is_active);
  const quickProducts = activeProducts.filter((p) => (p.use_count ?? 0) > 0).slice(0, 5);
  // Linking copies the product's unit onto the row but keeps the amount, so only offer
  // products in the row's own unit (or any product while nothing has been measured).
  // The draft amount counts: a typed value may not have been saved yet.
  const linkOptions = (app: Application) =>
    activeProducts.filter(
      (p) =>
        Number(draftById[app.id]?.amount ?? app.amount) === 0 ||
        p.unit.toLowerCase() === (app.unit ?? '').toLowerCase()
    );

  // Create mutation
  const createMutation = useMutation({
    scope: writeScope,
    // POST isn't idempotent: a retry after a lost response would log the product twice.
    retry: false,
    mutationFn: async (data: Partial<ApplicationCreate>) => {
      return api.post(
        `/api/v1/farms/${membership?.farmId}/applications`,
        {
          cycle_id: cycleId,
          application_date: dateStr,
          ...data,
        },
        { signal: writeSignal() }
      );
    },
    onMutate: async (data) => {
      if (!cycleId) return undefined;
      await queryClient.cancelQueries({ queryKey: applicationsKey });
      const previous = queryClient.getQueryData<Application[]>(applicationsKey);
      const nowIso = new Date().toISOString();
      const product = products.find((p) => p.id === data.product_id);
      const optimistic: Application = {
        id: `optimistic-${randomId()}`,
        farm_id: membership?.farmId,
        cycle_id: cycleId,
        application_date: dateStr,
        product_id: data.product_id ?? null,
        product_name: product?.name ?? '',
        product_category: product?.category ?? 'other',
        amount: data.amount != null ? Number(data.amount) : 0,
        unit: product?.unit ?? '',
        reason: data.reason ?? null,
        notes: data.notes ?? null,
        technician_id: null,
        created_at: nowIso,
        last_modified: nowIso,
        deleted_at: null,
      };
      const next = previous ? [...previous, optimistic] : [optimistic];
      queryClient.setQueryData(applicationsKey, next);
      return { previous, optimisticId: optimistic.id };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(applicationsKey, context.previous);
      }
      // A timed-out create may have committed: let the list show it.
      if (isUnconfirmedWrite(err)) {
        queryClient.invalidateQueries({ queryKey: applicationsKey });
        toast.error(createFailureMessage(err, 'Could not add the product'), { id: 'application-create-error' });
      }
      if (context?.optimisticId) {
        delete pendingChangesRef.current[context.optimisticId];
        delete typedRef.current[context.optimisticId];
      }
      // Usually a product deactivated since the list was fetched: refresh the picker.
      queryClient.invalidateQueries({ queryKey: qk.products.list(farmId) });
    },
    onSuccess: (result, _vars, context) => {
      refreshRelated();
      const created = (result as { data?: Application })?.data ?? (result as Application | undefined);
      const current = queryClient.getQueryData<Application[]>(applicationsKey) || [];
      let next = current;
      if (context?.optimisticId) {
        next = current.filter((a) => a.id !== context.optimisticId);
      }
      if (created) {
        next = [...next, created];
      }
      queryClient.setQueryData(applicationsKey, next);
      if (created && context?.optimisticId) {
        // Same card key as the optimistic row, so the swap doesn't remount it and drop
        // the focus and the text being typed.
        setRowKeyById((prev) => ({ ...prev, [created.id]: context.optimisticId }));
        const typed = typedRef.current[context.optimisticId];
        delete typedRef.current[context.optimisticId];
        if (typed) typedRef.current[created.id] = typed;
        setDraftById((prev) => {
          if (!(context.optimisticId in prev)) return prev;
          const { [context.optimisticId]: draft, ...rest } = prev;
          return { ...rest, [created.id]: draft };
        });
        if (pendingChangesRef.current[context.optimisticId]) {
          const pending = pendingChangesRef.current[context.optimisticId];
          const nextPending = { ...pendingChangesRef.current };
          delete nextPending[context.optimisticId];
          nextPending[created.id] = pending;
          pendingChangesRef.current = nextPending;
          setHasPending(true);
          setSaveStatus('saving');
          flushNow();
        }
      }
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    scope: writeScope,
    mutationFn: async ({ id, ...data }: { id: string } & Partial<ApplicationUpdate>) => {
      return api.put(`/api/v1/farms/${membership?.farmId}/applications/${id}`, data, {
        signal: writeSignal(),
      });
    },
    onMutate: async ({ id, ...data }) => {
      setSaveStatus('saving');
      if (!cycleId) return undefined;
      await queryClient.cancelQueries({ queryKey: applicationsKey });
      const previous = queryClient.getQueryData<Application[]>(applicationsKey);
      const previousRow = previous?.find((a) => a.id === id);
      if (previous) {
        const next = previous.map((app) =>
          app.id === id ? { ...app, ...data } : app
        );
        queryClient.setQueryData(applicationsKey, next);
      }
      return { previousRow };
    },
    onError: (err, vars, context) => {
      setSaveStatus('error');
      // A save sent on leave has no indicator left to show the failure.
      toast.error(saveFailureMessage(err, 'Unable to save application'), { id: 'application-save-error' });
      // Only revert the failed row, not the entire list
      if (context?.previousRow) {
        queryClient.setQueryData(applicationsKey, (old: Application[] | undefined) => {
          if (!old) return old;
          return old.map((item) =>
            item.id === vars.id ? context.previousRow! : item
          );
        });
      }
    },
    onSuccess: (result) => {
      setSaveStatus('saved');
      refreshRelated();
      const updated = (result as { data?: Application })?.data ?? (result as Application | undefined);
      if (updated) {
        const current = queryClient.getQueryData<Application[]>(applicationsKey) || [];
        const next = current.map((app) =>
          app.id === updated.id ? updated : app
        );
        queryClient.setQueryData(applicationsKey, next);
      }
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    scope: writeScope,
    mutationFn: async (id: string) => {
      return api.delete(`/api/v1/farms/${membership?.farmId}/applications/${id}`, {
        signal: writeSignal(),
      });
    },
    onMutate: async (id) => {
      if (!cycleId) return undefined;
      await queryClient.cancelQueries({ queryKey: applicationsKey });
      const previous = queryClient.getQueryData<Application[]>(applicationsKey);
      if (previous) {
        queryClient.setQueryData(applicationsKey, previous.filter((a) => a.id !== id));
      }
      setDraftById((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      delete typedRef.current[id];
      if (pendingChangesRef.current[id]) {
        const next = { ...pendingChangesRef.current };
        delete next[id];
        pendingChangesRef.current = next;
        setHasPending(Object.keys(next).length > 0);
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(applicationsKey, context.previous);
      }
    },
    onSuccess: () => {
      refreshRelated();
    },
  });

  const flushPending = useCallback(() => {
    const pending = pendingChangesRef.current;
    const ids = Object.keys(pending);
    if (!ids.length) return;

    const remaining: Record<string, Partial<Application>> = {};
    ids.forEach((id) => {
      if (id.startsWith('optimistic-')) {
        remaining[id] = pending[id];
        return;
      }
      updateMutation.mutate({ id, ...pending[id] });
    });

    pendingChangesRef.current = remaining;
    setHasPending(Object.keys(remaining).length > 0);
  }, [updateMutation]);

  const { signal: scheduleSave, flush: flushNow } = useDebouncedSignal(flushPending, {
    delayMs: 600,
    flushOnHide: true,
  });

  // No deps: flushAllPendingAutosaves empties the registry, so register again on every render.
  useEffect(() => registerAutosaveFlusher(flushNow));

  const queueFieldSave = useCallback(
    (app: Application, field: keyof Application, value: unknown) => {
      const appId = app.id;
      const currentValue = app[field];

      setDraftById((prev) => ({
        ...prev,
        [appId]: {
          ...prev[appId],
          [field]: value as Application[keyof Application],
        },
      }));

      if (sameFieldValue(value, currentValue)) {
        const existing = pendingChangesRef.current[appId];
        if (existing && field in existing) {
          const next = { ...pendingChangesRef.current };
          const nextFields = { ...existing };
          delete nextFields[field];
          if (Object.keys(nextFields).length) {
            next[appId] = nextFields;
          } else {
            delete next[appId];
          }
          pendingChangesRef.current = next;
          setHasPending(Object.keys(next).length > 0);
        }
        return;
      }

      pendingChangesRef.current = {
        ...pendingChangesRef.current,
        [appId]: {
          ...pendingChangesRef.current[appId],
          [field]: value as Application[keyof Application],
        },
      };
      setHasPending(true);
      setSaveStatus('saving');
      scheduleSave();
    },
    [scheduleSave]
  );

  // The debounce is cancelled on unmount, and a focused field never blurs when its route is
  // torn down (browser back with the keyboard open), so an edit made inside the 600 ms
  // window would be dropped. The cleanup runs before the reset effect wipes the buffer.
  const leaveRef = useRef<() => void>(() => {});
  useEffect(() => {
    leaveRef.current = () => {
      applications?.forEach((app) => {
        const typed = typedRef.current[app.id];
        if (!typed) return;
        if (typed.amount !== undefined) queueFieldSave(app, 'amount', typed.amount ?? app.amount);
        if (typed.notes !== undefined) queueFieldSave(app, 'notes', typed.notes);
      });
      typedRef.current = {};
      flushNow();
    };
  });
  useEffect(() => () => leaveRef.current(), [resetKey]);

  const draftsRef = useRef(drafts);
  const resetKeyRef = useRef(resetKey);
  useEffect(() => {
    draftsRef.current = drafts;
    resetKeyRef.current = resetKey;
  });

  useUnsavedEditsWarning(
    () =>
      Object.values(typedRef.current).some((t) => t.amount !== undefined || t.notes !== undefined) ||
      Object.keys(pendingChangesRef.current).length > 0 ||
      queryClient.isMutating() > 0 ||
      draftsRef.current.length > 0
  );

  // A new application lives only in this page until it is saved, so leaving inside the app asks first.
  useBlocker({
    shouldBlockFn: () => !window.confirm('Not saved yet. Leave anyway?'),
    disabled: drafts.length === 0,
  });

  const commitField = (app: Application, field: 'amount' | 'notes', value: unknown) => {
    const typed = typedRef.current[app.id];
    if (typed) delete typed[field];
    queueFieldSave(app, field, value);
    flushNow();
  };

  const handleAddProduct = (product: FarmProductResponse) => {
    setDrafts((prev) => [...prev, { key: randomId(), product, amount: null, notes: '' }]);
  };

  const updateDraft = (key: string, changes: Partial<Pick<NewApplicationDraft, 'amount' | 'notes'>>) =>
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...changes } : d)));

  const saveDraft = (draft: NewApplicationDraft) => {
    if (draft.amount === null || !(draft.amount > 0)) return;
    setDrafts((prev) => prev.filter((d) => d.key !== draft.key));
    const savedFor = resetKey;
    createMutation
      .mutateAsync({
        product_id: draft.product.id,
        amount: draft.amount,
        ...(draft.notes.trim() ? { notes: draft.notes.trim() } : {}),
        // A retry of this same draft after a lost response returns the row it already made; a
        // corrected amount or note is a new intent and gets a new key.
        idempotency_key: idempotency.keyFor(
          JSON.stringify([cycleId, dateStr, draft.product.id, draft.amount, draft.notes.trim()]),
          draft.key
        ),
      })
      // A failed create keeps what was typed, so nothing has to be entered twice. Not on another
      // day or crop: the draft belongs to the one it was saved for.
      .catch(() => {
        if (resetKeyRef.current === savedFor) setDrafts((prev) => [...prev, draft]);
      });
  };

  const handlePickProduct = (productId: string) => {
    const product = activeProducts.find((p) => p.id === productId);
    if (product) handleAddProduct(product);
  };

  if (membershipLoading || pondLoading || applicationsLoading || productsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate({ to: `/entry/pond/${pondId}`, search: { date: dateStr } })}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-50">
                <FlaskConical className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{pond?.name}</h1>
                <p className="text-sm text-gray-500">
                  Applications • {format(displayDate, 'EEEE, MMMM d')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={flushNow}
            disabled={!hasPending || updateMutation.isPending}
          >
            Save All
          </Button>
          <AutoSaveIndicator
            status={saveStatus}
            onRetry={() => updateMutation.reset()}
          />
        </div>
      </div>

      {/* Quick Add from Recent */}
      {quickProducts.length > 0 && (
        <div className="bg-teal-50/50 rounded-xl border border-teal-100 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-teal-600" />
            <h3 className="text-sm font-medium text-teal-900">Recent Products</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {quickProducts.map((product) => (
              <button
                key={product.id}
                onClick={() => handleAddProduct(product)}
                disabled={createMutation.isPending}
                className="px-3 py-1.5 text-sm bg-white border border-teal-200 rounded-full text-teal-700 hover:bg-teal-50 hover:border-teal-300 transition-colors"
              >
                + {product.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Applications List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">
            {isToday ? "Today's applications" : `Applications on ${format(displayDate, 'MMM d')}`} ({applications?.length || 0})
          </h2>
        </div>

        {productsError ? (
          <p className="text-sm font-medium text-status-critical">
            Couldn't load the product list. Refresh the page to try again.
          </p>
        ) : activeProducts.length > 0 ? (
          <div className="max-w-sm">
            <SelectInput
              label="Add product"
              placeholder="Choose a product…"
              value=""
              options={activeProducts.map((p) => ({ value: p.id, label: `${p.name} (${p.unit})` }))}
              onChange={handlePickProduct}
              disabled={createMutation.isPending}
            />
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            {canDelete
              ? 'No products yet. Add them in Settings › Products.'
              : 'No products yet. Ask a Farm Technician to add products.'}
          </p>
        )}
        {createMutation.error && (
          <p className="text-sm font-medium text-status-critical">
            {createFailureMessage(createMutation.error, 'Could not add the product')}
          </p>
        )}

        {drafts.map((d) => (
          <div
            key={d.key}
            data-new-application
            className="overflow-hidden rounded-xl border border-dashed border-teal-300 bg-teal-50/40 shadow-sm"
          >
            <div className="flex items-center justify-between gap-3 border-b border-teal-100 px-5 py-3">
              <span className="break-words font-semibold text-gray-900">{d.product.name}</span>
              <span className="shrink-0 text-xs font-medium text-teal-700">New, not saved yet</span>
            </div>
            <div className="space-y-4 p-5">
              <NumericInput
                label={`Amount (${d.product.unit})`}
                value={d.amount}
                min={0}
                step={0.1}
                autoFocus
                onValueChange={(value) => updateDraft(d.key, { amount: value })}
                onBlur={(value) => updateDraft(d.key, { amount: value })}
                onRefusalChange={(refused) =>
                  setRefusedDrafts((prev) => (prev[d.key] === refused ? prev : { ...prev, [d.key]: refused }))
                }
              />
              <TextArea
                label="Notes"
                value={d.notes}
                onValueChange={(value) => updateDraft(d.key, { notes: value })}
                rows={2}
                placeholder="Application method, observations, etc."
              />
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setDrafts((prev) => prev.filter((x) => x.key !== d.key))}>
                  Cancel
                </Button>
                <Button
                  onClick={() => saveDraft(d)}
                  disabled={d.amount === null || !(d.amount > 0) || !!refusedDrafts[d.key]}
                  loading={createMutation.isPending}
                >
                  Save application
                </Button>
              </div>
            </div>
          </div>
        ))}

        {applications?.length === 0 && drafts.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center">
            <FlaskConical className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{isToday ? 'No applications recorded for today' : `No applications recorded for ${format(displayDate, 'MMM d')}`}</p>
          </div>
        ) : applications?.length === 0 ? null : (
          <div className="space-y-4">
            {applications?.map((app) => {
              const draft = draftById[app.id];
              const categoryValue = app.product_category;
              const amountValue = draft?.amount ?? app.amount;

              return (
                <div
                  key={rowKeyById[app.id] ?? app.id}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                >
                <div className="px-5 py-3 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
                  <span className={cn(
                    'text-xs font-medium px-2 py-1 rounded-full',
                    categoryValue === 'probiotic' && 'bg-green-100 text-green-700',
                    categoryValue === 'water_treatment' && 'bg-blue-100 text-blue-700',
                    categoryValue === 'chemical' && 'bg-amber-100 text-amber-700',
                    categoryValue === 'antibiotic' && 'bg-red-100 text-red-700',
                    !['probiotic', 'water_treatment', 'chemical', 'antibiotic'].includes(categoryValue) && 'bg-gray-100 text-gray-700'
                  )}>
                    {CATEGORY_OPTIONS.find((c) => c.value === categoryValue)?.label || categoryValue}
                  </span>
                  {canDelete && !app.id.startsWith('optimistic-') && (
                    <button
                      // Keep focus in the amount field: a blur here would PUT a row about to be deleted.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        const label = `${app.product_name || 'this entry'} ${app.amount ?? ''} ${app.unit ?? ''}`.trim();
                        if (window.confirm(`Delete ${label}? The change is recorded.`)) deleteMutation.mutate(app.id);
                      }}
                      className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                      {app.product_id || linkOptions(app).length === 0 ? (
                        <>
                          <p className="block text-sm font-medium text-gray-700">Product</p>
                          <p className="mt-1.5 break-words py-3 text-gray-900">{app.product_name}</p>
                          {!app.product_id && (
                            <p className="text-xs text-gray-500">
                              Not on the farm list, and no listed product uses {app.unit || 'this unit'}.
                            </p>
                          )}
                        </>
                      ) : (
                        <SelectInput
                          label={app.product_name ? `Product: ${app.product_name}` : 'Product'}
                          placeholder="Link to a farm product…"
                          value=""
                          options={linkOptions(app).map((p) => ({ value: p.id, label: `${p.name} (${p.unit})` }))}
                          disabled={app.id.startsWith('optimistic-') || updateMutation.isPending}
                          onChange={(productId) => updateMutation.mutate({ id: app.id, product_id: productId })}
                        />
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <NumericInput
                        label={app.unit ? `Amount (${app.unit})` : 'Amount'}
                        value={toNumberOrNull(amountValue)}
                        min={0}
                        step={0.1}
                        onValueChange={(value) => {
                          typedRef.current[app.id] = { ...typedRef.current[app.id], amount: value };
                        }}
                        onBlur={(value) => commitField(app, 'amount', value ?? app.amount)}
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <TextArea
                      label="Notes"
                      value={(draft?.notes ?? app.notes) || ''}
                      onValueChange={(value) => {
                        typedRef.current[app.id] = { ...typedRef.current[app.id], notes: value };
                      }}
                      onBlur={(value) => commitField(app, 'notes', value)}
                      rows={2}
                      placeholder="Application method, observations, etc."
                    />
                  </div>
                </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <Link
          to="/entry/pond/$pondId"
          params={{ pondId }}
          search={{ date: dateStr }}
          className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          ← Back to entry hub
        </Link>
      </div>
    </div>
  );
}
